/**
 * Shared React Query provider for all IPA portals.
 *
 * Creates a QueryClient with IPA-wide defaults and supplies it to the
 * React tree via QueryClientProvider. Each portal's App.tsx should wrap
 * its router in a single QueryProvider so all pages share a cache.
 *
 * Defaults rationale:
 * - `staleTime: 30s` — avoids refetch storms on rapid navigation while keeping
 *   data reasonably fresh for a dashboard UX.
 * - `gcTime: 5min` — retains cached responses across brief back/forward nav.
 * - `retry: 1` — one retry on transient failures; beyond that we surface an
 *   error state so the user can retry deliberately.
 * - `refetchOnWindowFocus: false` — prevents surprise reloads when users
 *   switch tabs (common in brokerage/carrier admin workflows).
 *
 * @module shared/api/QueryProvider
 */

import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

/**
 * Builds a QueryClient configured with IPA-wide defaults.
 *
 * Tests and advanced integrations may call this to obtain a fresh client
 * with the same defaults (e.g., to scope a QueryClientProvider per test).
 *
 * @returns A new QueryClient with IPA defaults applied.
 */
export function createDefaultQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        retry: 1,
        refetchOnWindowFocus: false,
      },
    },
  });
}

/** Shared default client used when no `client` prop is supplied to QueryProvider. */
const defaultClient = createDefaultQueryClient();

interface QueryProviderProps {
  /** React subtree that will gain access to the shared QueryClient. */
  children: React.ReactNode;
  /** Optional custom QueryClient — primarily for tests or per-portal overrides. */
  client?: QueryClient;
}

/**
 * Wraps children in a QueryClientProvider using the shared default client
 * (or a caller-supplied client).
 *
 * @param props - React children and optional client override.
 * @returns The wrapped React subtree.
 *
 * @example
 * ```tsx
 * export function App() {
 *   return (
 *     <QueryProvider>
 *       <BrowserRouter>
 *         <Routes>...</Routes>
 *       </BrowserRouter>
 *     </QueryProvider>
 *   );
 * }
 * ```
 */
export function QueryProvider({ children, client }: QueryProviderProps): React.ReactElement {
  return <QueryClientProvider client={client ?? defaultClient}>{children}</QueryClientProvider>;
}
